import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataError,
  IOption,
  LoadingIndicator,
  Select
} from "@digital-services/ui";
import { isNotEmpty, useInput } from "@ds-cps/ui";

import { useGetCountries } from "@organization/services/use-get-countries";

interface Props {
  label: string;
  selectedCountryCode: string | undefined;
  disabled?: boolean;
  onInputChange?: (
    newValue: string,
    isValid: boolean,
    errorMessage?: string
  ) => void;
}

const CountryDropDown: FunctionComponent<Props> = props => {
  const { label, selectedCountryCode, disabled = false, onInputChange } = props;

  const { t } = useTranslation(undefined, { keyPrefix: "countries" });
  const { t: tCommon } = useTranslation();

  const [options, setOptions] = useState<IOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<IOption | null>(null);

  const { countries, isLoading, error } = useGetCountries();

  const { isValid, errorMessage, valueChangeHandler } = useInput(
    [
      isNotEmpty(() =>
        tCommon("validation.string.not-empty", { fieldName: label })
      )
    ],
    selectedCountryCode
  );

  useEffect(() => {
    if (countries) {
      setOptions(
        countries.map(({ code, displayName }) => ({
          value: code, // goes here in UPPERCASE
          label: displayName
        }))
      );
    }
  }, [countries]);

  useEffect(() => {
    if (options.length > 0) {
      if (selectedCountryCode) {
        const foundOption = options.find(
          ({ value }) => value.toString() === selectedCountryCode.toUpperCase()
        );
        setSelectedOption(foundOption ?? null);
      }
    }
  }, [selectedCountryCode, options]);

  const handleChange = (option: IOption) => {
    setSelectedOption(option);
    const { value, isValid } = valueChangeHandler(option.value as string);
    if (onInputChange) onInputChange(value, isValid);
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  if (countries === undefined || countries.length === 0)
    return <div data-testid="country-no-data">{t("load.none.message")}</div>;

  return (
    <Select
      disabled={disabled}
      data-testid="country-dropdown"
      id="country-dropdown"
      error={!isValid}
      label={label}
      placeholder={label}
      helperText={isValid ? undefined : errorMessage}
      key="country"
      options={options}
      selected={selectedOption}
      onChange={handleChange}
      enableSearch={true}
    />
  );
};

export default CountryDropDown;
